import React from "react";
import { Button } from "@/components/ui/button";
import { Trash2, X, Loader2 } from "lucide-react";

/**
 * BulkActionBar — floating, minimal action strip that appears when one or more
 * rows are selected. Keeps layout from shifting by rendering inline above the
 * list, and stays out of the way when no items are selected.
 *
 * Props:
 *   count    — number of selected items (hidden when 0)
 *   label    — singular resource label, e.g. "invoice", "customer"
 *   onDelete — async () => void — deletes the selected items
 *   onClear  — () => void — clears the selection
 *   busy     — boolean — disables actions while in progress
 *   warning  — optional extra note, e.g. "also deletes linked payments"
 *   testId   — base data-testid prefix, default "bulk"
 */
export default function BulkActionBar({ count = 0, label = "item", onDelete, onClear, busy = false, warning, testId = "bulk" }) {
    if (!count) return null;
    const plural = count === 1 ? label : `${label}s`;
    return (
        <div
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-secondary/30 bg-secondary/5 px-4 py-3"
            data-testid={testId}
        >
            <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-secondary text-white grid place-items-center text-sm font-semibold" data-testid={`${testId}-count`}>
                    {count}
                </div>
                <div>
                    <div className="text-sm font-medium text-ocean-700">{count} {plural} selected</div>
                    {warning && <div className="text-xs text-muted-foreground mt-0.5">{warning}</div>}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onClear}
                    disabled={busy}
                    data-testid={`${testId}-clear`}
                    className="rounded-lg"
                >
                    <X className="h-4 w-4 mr-1" /> Clear
                </Button>
                <Button
                    type="button"
                    size="sm"
                    onClick={onDelete}
                    disabled={busy}
                    data-testid={`${testId}-delete`}
                    className="rounded-lg bg-coral hover:bg-coral-dark text-white"
                >
                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                    Delete {count} {plural}
                </Button>
            </div>
        </div>
    );
}
